import { gsap } from 'gsap';

/**
 * Members List Toggle
 * Handles show/hide functionality for the members list with height limitation
 */
export default class MembersListToggle {
    constructor() {
        // Animation configuration
        this.ANIMATION_CONFIG = {
            DURATION: 0.6,
            EASE: "power2.out",
            OVERLAY_DURATION: 0.3,
            OVERLAY_EASE: "power2.inOut"
        };

        this.container = document.getElementById('members-list-container');
        this.content = document.getElementById('members-list-content');
        this.overlay = document.getElementById('members-list-overlay');
        this.button = document.getElementById('show-all-members-btn');
        // Elements inside overlay
        this.gradient = this.overlay ? this.overlay.querySelector('.bg-gradient-to-t') : null;
        this.buttonContainer = this.overlay ? this.overlay.querySelector('.pointer-events-auto') : null;
        // Elements inside button
        this.buttonLabel = this.button ? this.button.querySelector('.members-btn-label') : null;
        this.verticalBar = this.button ? this.button.querySelector('.rotate-90') : null;

        if (!this.container || !this.content || !this.overlay || !this.button) {
            return;
        }

        this.isExpanded = false;
        this.originalButtonText = this.buttonLabel ? this.buttonLabel.textContent : this.button.textContent;
        this.maxHeight = 620; // 620px = max-h-155 equivalent
        this.EXPANDED_EXTRA_GAP_PX = 20; // Extra spacing between list and button when expanded
        this.fullHeight = null; // Cache for full content height
        this.lockedScrollPosition = null; // For maintaining scroll position during animation
        this.scrollLockHandler = null; // Bound scroll event handler
        this.originalContainerPaddingBottom = this.container ? this.container.style.paddingBottom : '';

        this.init();
    }

    init() {
        this.button.addEventListener('click', (event) => {
            event.preventDefault();
            this.toggle();
        });

        // Check if content needs the toggle functionality
        // If content height is less than max-height, hide the overlay (including the button)
        this.checkContentHeight();

        // Re-check on window resize
        window.addEventListener('resize', () => this.checkContentHeight());
    }

    checkContentHeight() {
        // Create a cloned element to measure height without affecting layout
        const clone = this.content.cloneNode(true);
        clone.style.position = 'absolute';
        clone.style.visibility = 'hidden';
        clone.style.maxHeight = 'none';
        clone.style.height = 'auto';

        // Append to parent to get accurate measurements
        this.container.appendChild(clone);
        const contentHeight = clone.scrollHeight;

        // Cache the full height for animations
        this.fullHeight = contentHeight;

        // Remove the clone
        this.container.removeChild(clone);

        if (contentHeight <= this.maxHeight) {
            // Content fits within max height, hide overlay and button
            this.overlay.style.display = 'none';
            // Reset padding in case it was set while expanded
            this.container.style.paddingBottom = this.originalContainerPaddingBottom || '';
        } else {
            // Content exceeds max height
            this.overlay.style.display = 'block';
            if (this.isExpanded) {
                // Keep gradient hidden and ensure container has padding so the button doesn't cover content
                if (this.gradient) {
                    this.gradient.style.display = 'none';
                    this.gradient.style.opacity = '0';
                }
                if (this.buttonContainer) {
                    const buttonHeight = this.buttonContainer.offsetHeight || 64;
                    this.container.style.paddingBottom = `${buttonHeight + this.EXPANDED_EXTRA_GAP_PX}px`;
                }
            } else {
                // Collapsed: show gradient and remove any extra padding
                if (this.gradient) {
                    this.gradient.style.display = 'block';
                    this.gradient.style.opacity = '1';
                }
                this.container.style.paddingBottom = this.originalContainerPaddingBottom || '';
            }
        }
    }

    toggle() {
        this.isExpanded = !this.isExpanded;

        if (this.isExpanded) {
            this.expand();
        } else {
            this.collapse();
        }
    }

    expand() {
        // Capture current scroll position to maintain it during animation
        this.lockedScrollPosition = window.pageYOffset || document.documentElement.scrollTop;

        // Create scroll lock handler
        this.scrollLockHandler = () => {
            if (this.lockedScrollPosition !== null) {
                window.scrollTo(0, this.lockedScrollPosition);
            }
        };

        // Add scroll event listener to prevent scroll changes during animation
        window.addEventListener('scroll', this.scrollLockHandler, { passive: false });

        // Use cached height to prevent layout shifts
        const fullHeight = this.fullHeight || this.content.scrollHeight;

        // Set initial GSAP state to prevent jumps before removing class
        gsap.set(this.content, { maxHeight: `${this.maxHeight}px` });

        // Remove height restriction
        this.content.classList.remove('max-h-155');

        // Animate height expansion with GSAP
        gsap.to(this.content, {
            maxHeight: `${fullHeight}px`,
            duration: this.ANIMATION_CONFIG.DURATION,
            ease: this.ANIMATION_CONFIG.EASE,
            onUpdate: () => {
                // Maintain scroll position throughout animation
                if (this.lockedScrollPosition !== null) {
                    window.scrollTo(0, this.lockedScrollPosition);
                }
            },
            onComplete: () => {
                // Release scroll lock after animation
                this.lockedScrollPosition = null;
                if (this.scrollLockHandler) {
                    window.removeEventListener('scroll', this.scrollLockHandler);
                    this.scrollLockHandler = null;
                }
            }
        });

        // Keep overlay visible for the button, only fade the gradient out
        if (this.overlay) {
            this.overlay.style.display = 'block';
        }
        if (this.gradient) {
            gsap.to(this.gradient, {
                opacity: 0,
                duration: this.ANIMATION_CONFIG.OVERLAY_DURATION,
                ease: this.ANIMATION_CONFIG.OVERLAY_EASE,
                onComplete: () => {
                    this.gradient.style.display = 'none';
                }
            });
        }
        // Add bottom padding so the absolute-positioned button does not cover content
        if (this.buttonContainer) {
            const buttonHeight = this.buttonContainer.offsetHeight || 64;
            this.container.style.paddingBottom = `${buttonHeight + this.EXPANDED_EXTRA_GAP_PX}px`;
        }

        // Update button state: text to "Show less", hide vertical bar to make minus
        if (this.buttonLabel) {
            this.buttonLabel.textContent = 'Show less';
        } else {
            this.button.textContent = 'Show less';
        }
        if (this.verticalBar) {
            this.verticalBar.style.display = 'none';
        }
    }

    collapse() {
        // Show overlay and prepare for animation (button remains visible)
        if (this.overlay) {
            this.overlay.style.display = 'block';
        }
        if (this.gradient) {
            this.gradient.style.display = 'block';
            this.gradient.style.opacity = '0';
        }

        // Animate height collapse with GSAP
        gsap.to(this.content, {
            maxHeight: `${this.maxHeight}px`,
            duration: this.ANIMATION_CONFIG.DURATION,
            ease: this.ANIMATION_CONFIG.EASE,
            onComplete: () => {
                // Add the CSS class back after animation completes
                this.content.classList.add('max-h-155');
            }
        });

        // Fade in gradient with GSAP
        if (this.gradient) {
            gsap.to(this.gradient, {
                opacity: 1,
                duration: this.ANIMATION_CONFIG.OVERLAY_DURATION,
                ease: this.ANIMATION_CONFIG.OVERLAY_EASE,
                delay: 0.1 // Small delay to let height animation start first
            });
        }
        // Remove bottom padding added during expansion
        this.container.style.paddingBottom = this.originalContainerPaddingBottom || '';

        // Update button state: restore original text and show vertical bar again
        if (this.buttonLabel) {
            this.buttonLabel.textContent = this.originalButtonText;
        } else {
            this.button.textContent = this.originalButtonText;
        }
        if (this.verticalBar) {
            this.verticalBar.style.display = '';
        }
    }
}